import Box from "@mui/material/Box";
import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";

const STACK_ID = "corner-stack";

/** The bottom-right column every {@link CornerStack} portals into, created on first use. */
function stackElement(): HTMLElement {
  const existing = document.getElementById(STACK_ID);
  if (existing) return existing;
  const element = document.createElement("div");
  element.id = STACK_ID;
  // Bottom-up: a lower `order` sits nearer the corner. The column passes clicks through; its items take them.
  Object.assign(element.style, {
    position: "fixed",
    right: "0.5rem",
    bottom: "0.5rem",
    zIndex: "9999",
    display: "flex",
    flexDirection: "column-reverse",
    alignItems: "flex-end",
    gap: "0.5rem",
    pointerEvents: "none",
  });
  document.body.appendChild(element);
  return element;
}

/**
 * Pins `children` to the bottom-right corner, stacked with every other item there instead of on top of it.
 *
 * @param order - Position in the stack, lowest nearest the corner.
 */
export default function CornerStack({ order, children }: { order: number; children: ReactNode }) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  useEffect(() => setContainer(stackElement()), []);
  if (!container) return null;
  return createPortal(<Box sx={{ order, pointerEvents: "auto" }}>{children}</Box>, container);
}
